import { DrawerSection } from '@components/ui/drawer/section';
import { useTranslation } from 'react-i18next';
import { RadioGroup } from '@components/ui/radio';
import { Button } from '@components/ui/button';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useSchedulerContext } from '@contexts/scheduler';
import {
  getEquipmentMembersFromForeman,
  mapEquipmentMembersToSchedulerSidebarCrewMembers,
} from '@contexts/scheduler/sidebar/helpers';
import { SchedulerSidebarConfirmOption } from '../option';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useAccess } from '@hooks/access';
import { useEffect } from 'react';

export const SchedulerSidebarConfirmEquipmentChanged = () => {
  const {
    currentSchedule,
    defaultEquipmentChanged,
    confirm: {
      equipmentChanged: { open, keep },
    },
    equipmentMembers,
    setState,
  } = useSchedulerSidebar();
  const access = useAccess();

  const {
    active: { schedule },
  } = useSchedulerContext();

  const { fieldUsers } = useDropdownsContext();

  useEffect(() => {
    if (!currentSchedule || !schedule) return;
    if (schedule.id === 'new') return;
    if (!defaultEquipmentChanged) return;
    const differentForeman = currentSchedule?.foreman?.id !== schedule?.foreman?.id;

    setState((prev) => ({
      ...prev,
      confirm: {
        ...prev.confirm,
        equipmentChanged: { ...prev.confirm.equipmentChanged, open: !differentForeman },
      },
    }));
  }, [currentSchedule, schedule]);

  const { t } = useTranslation('app', { keyPrefix: 'schedule.confirm.equipmentChanged' });
  const { t: tActions } = useTranslation('app', { keyPrefix: 'actions' });

  const handleConfirm = () => {
    const defaultEquipmentMembers = getEquipmentMembersFromForeman(
      schedule.foreman?.id,
      fieldUsers,
    );

    setState((prev) => ({
      ...prev,
      defaultEquipmentChanged: false,
      defaultEquipment: !prev.confirm.equipmentChanged.keep,
      confirm: {
        ...prev.confirm,
        equipmentChanged: { ...prev.confirm.equipmentChanged, open: false },
      },
      equipmentMembers: prev.confirm.equipmentChanged.keep
        ? prev.equipmentMembers
        : mapEquipmentMembersToSchedulerSidebarCrewMembers(defaultEquipmentMembers),
    }));
  };

  const assignedFieldUser = fieldUsers.options.find((el) => el.id === schedule.foreman?.id) || null;

  const currentNames = equipmentMembers.map((el) => el.name);
  const defaultNames = mapEquipmentMembersToSchedulerSidebarCrewMembers(
    getEquipmentMembersFromForeman(schedule?.foreman?.id, fieldUsers),
  ).map((el) => el.name);

  if (!open) return null;
  return (
    <DrawerSection title={t('title')} alwaysOpen>
      <div className="flex flex-col gap-4">
        <p className="text-sm font-normal text-neutral-500">
          {t('message', { fieldUser: assignedFieldUser?.fullName || 'foreman' })}
        </p>
        <RadioGroup
          value={keep ? 'keep' : 'refresh'}
          disabled={access.readOnly}
          onValueChange={(v) => {
            setState((prev) => ({
              ...prev,
              confirm: {
                ...prev.confirm,
                equipmentChanged: { ...prev.confirm.equipmentChanged, keep: v === 'keep' },
              },
            }));
          }}
          className="flex flex-col gap-2">
          <SchedulerSidebarConfirmOption
            id="equipment-keep"
            value="keep"
            label={t('keep')}
            items={currentNames}
            emptyLabel={t('empty')}
          />
          <SchedulerSidebarConfirmOption
            id="equipment-refresh"
            value="refresh"
            label={t('refresh', { fieldUser: assignedFieldUser?.fullName || '' })}
            items={defaultNames}
            emptyLabel={t('empty')}
          />
        </RadioGroup>
        <div className="flex justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            type="button"
            size="sm"
            onClick={() => {
              setState((prev) => ({
                ...prev,
                defaultEquipmentChanged: false,
                defaultEquipment: false,
                confirm: {
                  ...prev.confirm,
                  equipmentChanged: { ...prev.confirm.equipmentChanged, keep: true, open: false },
                },
              }));
            }}>
            {tActions('cancel')}
          </Button>
          <Button disabled={access.readOnly} type="button" size="sm" onClick={handleConfirm}>
            {tActions('confirm')}
          </Button>
        </div>
      </div>
    </DrawerSection>
  );
};
